
import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import {
  DatasourceDownstreamMetricsQuery,
  DatasourceDownstreamMetricsQueryVariables,
} from '../../../gen-src/queries/DatasourceDownstreamMetricsQuery';
import {
  DatasourceDownstreamMetricsWithoutPersonalSpaceQuery,
  DatasourceDownstreamMetricsWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/DatasourceDownstreamMetricsWithoutPersonalSpaceQuery';
import {
  DownstreamMetricsFilteredByFieldQuery,
  DownstreamMetricsFilteredByFieldQuery_fieldsConnection_nodes,
  DownstreamMetricsFilteredByFieldQueryVariables,
} from '../../../gen-src/queries/DownstreamMetricsFilteredByFieldQuery';
import {
  DownstreamMetricsFilteredByFieldWithoutPersonalSpaceQuery,
  DownstreamMetricsFilteredByFieldWithoutPersonalSpaceQuery_fieldsConnection_nodes,
  DownstreamMetricsFilteredByFieldWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/DownstreamMetricsFilteredByFieldWithoutPersonalSpaceQuery';
import { MetricsDisplayGridRowDataFragment } from '../../../gen-src/queries/MetricsDisplayGridRowDataFragment';
import { MetricsDisplayGridRowDataFragmentWithoutPersonalSpace } from '../../../gen-src/queries/MetricsDisplayGridRowDataFragmentWithoutPersonalSpace';
import { MetricSortOrder } from '../../../gen-src/queries/globalTypes';
import { DatasourceDownstreamMetricsQueryGql } from '../../queries/DatasourceDownstreamMetricsQueryGql';
import { DatasourceDownstreamMetricsWithoutPersonalSpaceQueryGql } from '../../queries/DatasourceDownstreamMetricsWithoutPersonalSpaceQueryGql';
import { DownstreamMetricsFilteredByFieldQueryGql } from '../../queries/DownstreamMetricsFilteredByFieldQueryGql';
import { DownstreamMetricsFilteredByFieldWithoutPersonalSpaceQueryGql } from '../../queries/DownstreamMetricsFilteredByFieldWithoutPersonalSpaceQueryGql';
import { getRowDataForMetric, getRowDataForMetricWithoutPersonalSpace, MetricsDisplayGrid, MetricsDisplayGridRowData } from '../sharedDisplayGrid/MetricsDisplayGrid';
import { MetricsValidSortFields } from '../sorting/sortProviders/MetricsGridSortProvider';

type QueryVariables = DatasourceDownstreamMetricsQueryVariables;
type QueryResults = DatasourceDownstreamMetricsQuery;
type QueryResultListNode = MetricsDisplayGridRowDataFragment;
type GridRowData = MetricsDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = DatasourceDownstreamMetricsWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = DatasourceDownstreamMetricsWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = MetricsDisplayGridRowDataFragmentWithoutPersonalSpace;

type QueryVariablesWithFilter = DownstreamMetricsFilteredByFieldQueryVariables;
type QueryResultsWithFilter = DownstreamMetricsFilteredByFieldQuery;

type QueryVariablesWithFilterWithoutPersonalSpace = DownstreamMetricsFilteredByFieldWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = DownstreamMetricsFilteredByFieldWithoutPersonalSpaceQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.publishedDatasources && resultData.publishedDatasources[0] &&
    resultData.publishedDatasources[0].downstreamMetricsConnection;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.publishedDatasources && resultData.publishedDatasources[0] &&
    resultData.publishedDatasources[0].downstreamMetricsConnection;
}

function selectConnectionListResultForFilteredQuery(resultData: QueryResultsWithFilter):
  ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.fieldsConnection && resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] && getMetricsConnection(resultData.fieldsConnection.nodes[0]);
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(resultData: QueryResultsWithFilterWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.fieldsConnection && resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] && getMetricsConnectionWithoutPersonalSpace(resultData.fieldsConnection.nodes[0]);
}

function getMetricsConnection(field: DownstreamMetricsFilteredByFieldQuery_fieldsConnection_nodes) {
  return 'downstreamMetricsConnection' in field ? field.downstreamMetricsConnection : null;
}

function getMetricsConnectionWithoutPersonalSpace(
  field: DownstreamMetricsFilteredByFieldWithoutPersonalSpaceQuery_fieldsConnection_nodes) {
  return 'downstreamMetricsConnection' in field ? field.downstreamMetricsConnection : null;
}

export type DatasourceDownstreamMetricsQueryGridProps =
  GridSortProps<MetricsValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly filteredFieldId: string | null;
    readonly rowCount: number | null;
  };

export function DatasourceDownstreamMetricsQueryGrid(props: DatasourceDownstreamMetricsQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyDownstreamMetricsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredMetricsLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredFieldId ? (
      // Query with Selected Field as Filter
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
        queryGql={DownstreamMetricsFilteredByFieldQueryGql}
        partialQueryVariables={{ selectedFieldId: props.filteredFieldId, orderBy: props.orderBy as MetricSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForMetric}
      >
        {({ rowStore }) => <MetricsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
        // Query without Selection
        <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
          queryGql={DatasourceDownstreamMetricsQueryGql}
          partialQueryVariables={{ vizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as MetricSortOrder }}
          selectConnectionListResult={selectConnectionListResult}
          mapResultNode={getRowDataForMetric}
        >
          {({ rowStore }) => <MetricsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />}
        </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredFieldId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={DownstreamMetricsFilteredByFieldWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ selectedFieldId: props.filteredFieldId, orderBy: props.orderBy as MetricSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForMetricWithoutPersonalSpace}
      >
        {({ rowStore }) => <MetricsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
        <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
        QueryResultListNodeWithoutPersonalSpace, GridRowData>
          queryGql={DatasourceDownstreamMetricsWithoutPersonalSpaceQueryGql}
          partialQueryVariables={{ vizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as MetricSortOrder }}
          selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
          mapResultNode={getRowDataForMetricWithoutPersonalSpace}
        >
          {({ rowStore }) => <MetricsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />}
        </PaginatedQueryWrapper>
    )
  );
}
